/* istanbul ignore file */
import { FormRow } from 'framework/ui/layout';
import { CurrencyDropdown } from 'domain/shared/core/input';
import { TextInput, Checkbox } from 'framework/form';
import { Box } from '@mui/material';
import {
  BaseAmountTypeDropdown,
  EmployeeContractTypeDropdown,
  OfficeDropdown,
  ProbationPeriodReductionInput,
} from 'domain/shared/employee';
import { CREATE_NEW_OFFER_TOOLTIP, isFilled } from 'domain/shared/offerCompensation';
import type { OfferCompensationDetails } from '../model';

interface OfferCompensationDetailsInputProps {
  source: string;
  initialValue?: OfferCompensationDetails;
  isUpdate?: boolean;
  canViewBaseFinanceFields?: boolean;
  canViewCalculatedGrossField?: boolean;
  canViewProbationField?: boolean;
}

const OfferCompensationDetailsInput = ({
  source,
  initialValue = {},
  isUpdate = false,
  canViewBaseFinanceFields = true,
  canViewCalculatedGrossField = true,
  canViewProbationField = true,
}: OfferCompensationDetailsInputProps) => {
  const isReadOnly = (field: keyof OfferCompensationDetails) => isUpdate && isFilled(initialValue[field]);

  return (
    <Box sx={{ width: { xs: '100%', sm: '95%' } }}>
      <FormRow>
        <BaseAmountTypeDropdown
          source={`${source}.baseAmountType`}
          label="Base amount type"
          isVisible={canViewBaseFinanceFields}
          disabled={isReadOnly('baseAmountType')}
          tooltip={isReadOnly('baseAmountType') ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
        <CurrencyDropdown
          source={`${source}.currency`}
          label="Currency"
          isVisible={canViewBaseFinanceFields}
          disabled={isReadOnly('currency')}
          tooltip={isReadOnly('currency') ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
        <TextInput
          source={`${source}.grossAmount`}
          label="Final amount gross"
          isVisible={canViewBaseFinanceFields}
          disabled={isReadOnly('grossAmount')}
          tooltip={isReadOnly('grossAmount') ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
        <TextInput
          source={`${source}.grossProbationAmount`}
          label="Probation amount gross"
          isVisible={canViewProbationField}
          disabled={isReadOnly('grossProbationAmount')}
          tooltip={isReadOnly('grossProbationAmount') ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
        <TextInput
          source={`${source}.calculatedGrossAmount`}
          label="Calculated amount gross"
          tooltip={
            isReadOnly('calculatedGrossAmount') ? CREATE_NEW_OFFER_TOOLTIP : 'Amount calculated by qualification model'
          }
          isVisible={canViewCalculatedGrossField}
          disabled={isReadOnly('calculatedGrossAmount')}
        />
        <ProbationPeriodReductionInput
          source={`${source}.probationReduction`}
          probationAmountSource={`${source}.grossProbationAmount`}
          baseAmountSource={`${source}.grossAmount`}
          label="Probation period reduction, %"
          isVisible={canViewProbationField}
        />
        <OfficeDropdown
          source={`${source}.officeId`}
          label="Office"
          disabled={isReadOnly('officeId')}
          tooltip={isReadOnly('officeId') ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
        <EmployeeContractTypeDropdown
          source={`${source}.contractType`}
          label="Contract type"
          disabled={isReadOnly('contractType')}
          tooltip={isReadOnly('contractType') ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
        <Checkbox
          source={`${source}.isAccepted`}
          label="Is compensation accepted?"
          disabled={formData => !formData.id}
        />
      </FormRow>
    </Box>
  );
};

export default OfferCompensationDetailsInput;
